/**
 * FactoryJourneyOwnership (AJIP-16.02.009 · §10, §25)
 * ---------------------------------------------------------------------
 * Runtime, deterministic evaluation of factory representation authority.
 *
 * Directive requirements:
 *   §10  A user's association with a supplier must NOT automatically
 *        authorise control over a factory. Factory control MUST be
 *        established through an explicit authorised relationship.
 *   §25  Authorisation is enforced at the boundary. Client-supplied
 *        identifiers are never trusted without server-side validation
 *        (this module accepts a caller-supplied provider — server-side
 *        validation is the responsibility of the provider).
 *
 * Outcome contract (frozen):
 *   {
 *     decision: 'AUTHORISED' | 'DENIED' | 'RELATIONSHIP_UNCONFIRMED'
 *              | 'SUBJECT_UNKNOWN' | 'INPUT_INVALID',
 *     principalRef, organisationRef, factorySubjectRef,
 *     grantedPermissions, deniedReasons, evidence, evaluatedAt
 *   }
 *
 * Fail-closed behaviour: any provider throw degrades to
 * RELATIONSHIP_UNCONFIRMED. Never re-thrown.
 */
import { FactoryOwnershipAuthorityError } from './FactoryJourneyErrors';

const FACTORY_OWNERSHIP_DECISIONS = Object.freeze({
  AUTHORISED: 'AUTHORISED',
  DENIED: 'DENIED',
  RELATIONSHIP_UNCONFIRMED: 'RELATIONSHIP_UNCONFIRMED',
  SUBJECT_UNKNOWN: 'SUBJECT_UNKNOWN',
  INPUT_INVALID: 'INPUT_INVALID',
});

const FACTORY_OWNERSHIP_PROVIDER_METHODS = Object.freeze([
  'lookupFactorySubject',
  'evaluateFactoryRepresentation',
  'listFactoryPermissions',
]);

const assertFactoryOwnershipProvider = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    throw new FactoryOwnershipAuthorityError('FactoryOwnershipProvider: candidate must be non-null object');
  }
  for (const m of FACTORY_OWNERSHIP_PROVIDER_METHODS) {
    if (typeof candidate[m] !== 'function') {
      throw new FactoryOwnershipAuthorityError(`FactoryOwnershipProvider: missing method '${m}'`);
    }
  }
  return true;
};

const _freezeOutcome = (o) => Object.freeze({
  decision: o.decision,
  principalRef: o.principalRef,
  organisationRef: o.organisationRef,
  factorySubjectRef: o.factorySubjectRef,
  grantedPermissions: Object.freeze([...(o.grantedPermissions || [])]),
  deniedReasons: Object.freeze([...(o.deniedReasons || [])]),
  evidence: Object.freeze([...(o.evidence || [])]),
  evaluatedAt: o.evaluatedAt,
});

const evaluateFactoryOwnership = ({
  principalRef, organisationRef, factorySubjectRef,
  provider, nowFn = () => Date.now(),
}) => {
  const now = nowFn();
  const inputs = { principalRef, organisationRef, factorySubjectRef };
  const missing = [];
  for (const [k, v] of Object.entries(inputs)) {
    if (typeof v !== 'string' || v.length === 0) missing.push(k);
  }
  if (missing.length > 0) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.INPUT_INVALID,
      principalRef: principalRef || '', organisationRef: organisationRef || '',
      factorySubjectRef: factorySubjectRef || '',
      grantedPermissions: [], deniedReasons: [`missing:${missing.join(',')}`],
      evidence: [], evaluatedAt: now,
    });
  }
  if (!provider) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.RELATIONSHIP_UNCONFIRMED,
      principalRef, organisationRef, factorySubjectRef,
      grantedPermissions: [], deniedReasons: ['no-provider'],
      evidence: [], evaluatedAt: now,
    });
  }
  assertFactoryOwnershipProvider(provider);
  let subject;
  try { subject = provider.lookupFactorySubject(factorySubjectRef); }
  catch (_e) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.RELATIONSHIP_UNCONFIRMED,
      principalRef, organisationRef, factorySubjectRef,
      grantedPermissions: [], deniedReasons: ['lookupFactorySubject-threw'],
      evidence: [], evaluatedAt: now,
    });
  }
  if (subject === null || subject === undefined) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.SUBJECT_UNKNOWN,
      principalRef, organisationRef, factorySubjectRef,
      grantedPermissions: [], deniedReasons: ['factory-subject-not-found'],
      evidence: [], evaluatedAt: now,
    });
  }
  let representation;
  try {
    representation = provider.evaluateFactoryRepresentation({
      principalRef, organisationRef, factorySubjectRef, subject,
    });
  } catch (_e) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.RELATIONSHIP_UNCONFIRMED,
      principalRef, organisationRef, factorySubjectRef,
      grantedPermissions: [], deniedReasons: ['evaluateFactoryRepresentation-threw'],
      evidence: [], evaluatedAt: now,
    });
  }
  if (!representation || representation.authorised !== true) {
    return _freezeOutcome({
      decision: FACTORY_OWNERSHIP_DECISIONS.DENIED,
      principalRef, organisationRef, factorySubjectRef,
      grantedPermissions: [],
      deniedReasons: [String((representation && representation.reason) || 'not-authorised')],
      evidence: representation && representation.evidence ? [...representation.evidence] : [],
      evaluatedAt: now,
    });
  }
  let perms = [];
  try {
    perms = provider.listFactoryPermissions({
      principalRef, organisationRef, factorySubjectRef, subject,
    }) || [];
  } catch (_e) { perms = []; }
  if (!Array.isArray(perms)) perms = [];
  return _freezeOutcome({
    decision: FACTORY_OWNERSHIP_DECISIONS.AUTHORISED,
    principalRef, organisationRef, factorySubjectRef,
    grantedPermissions: perms.map(String),
    deniedReasons: [],
    evidence: representation.evidence ? [...representation.evidence] : [],
    evaluatedAt: now,
  });
};

const isFactoryAuthorised = (outcome) => outcome && outcome.decision === FACTORY_OWNERSHIP_DECISIONS.AUTHORISED;

export {
  FACTORY_OWNERSHIP_DECISIONS,
  FACTORY_OWNERSHIP_PROVIDER_METHODS,
  assertFactoryOwnershipProvider,
  evaluateFactoryOwnership,
  isFactoryAuthorised,
};
